'use client';
// Cash row + weighted-average portfolio totals row for HoldingsList.
// Extracted to keep the parent under the 500-line god-file threshold.

import {
  compTier,
  PnlPortfolioCell,
  INDEX_ETFS,
} from './HoldingsList.helpers';
import type { Holding } from './HoldingsList.types';
import type { ScoreRow } from '@/lib/hooks/use-holdings-data';

interface Props {
  filterMode: 'all' | 'sell';
  editing: boolean;
  gridColsCls: string;
  showLots: boolean;
  portfolio: { cashWeight?: number } | null;
  holdings: Holding[];
  sorted: Holding[];
  scores: Map<string, ScoreRow> | null;
  totalWeight: number;
}

/** Weight-coverage-aware average. Holdings that lack a value for a column
 *  drop out — so a 70% populated 1y column averages over those 70% only. */
function weightedAvg(sorted: Holding[], getter: (h: Holding) => number | null): { value: number | null; coverage: number } {
  let sumW = 0, sumWX = 0, totalW = 0;
  for (const h of sorted) {
    totalW += h.weight;
    const v = getter(h);
    if (v == null) continue;
    sumW += h.weight;
    sumWX += h.weight * v;
  }
  if (sumW === 0 || totalW === 0) return { value: null, coverage: 0 };
  return { value: sumWX / sumW, coverage: sumW / totalW };
}

export function HoldingsTotalsRow({
  filterMode, editing, gridColsCls, showLots,
  portfolio, holdings, sorted, scores, totalWeight,
}: Props) {
  if (filterMode !== 'all') return null;
  const totals = {
    composite: weightedAvg(sorted, h => h.composite),
    day: weightedAvg(sorted, h => h.pnl.dayPct),
    mtd: weightedAvg(sorted, h => h.pnl.mtdPct),
    ytd: weightedAvg(sorted, h => h.pnl.ytdPct),
    off52: weightedAvg(sorted, h => h.pnl.off52wHigh),
    q: weightedAvg(sorted, h => h.q),
    v: weightedAvg(sorted, h => h.v),
    qPct: weightedAvg(sorted, h => h.selfPctile.q),
    vPct: weightedAvg(sorted, h => h.selfPctile.v),
    cPct: weightedAvg(sorted, h => h.selfPctile.c),
  };
  return (
    <>
      {/* Cash row — only shown when user-set weights leave a non-zero remainder. */}
      {!editing && portfolio?.cashWeight != null && portfolio.cashWeight > 0.001 && (
        <div className={`grid ${gridColsCls} gap-1 px-2 h-[25px] text-[9px] leading-tight items-center border-b border-[#1e293b]/60 text-[#94a3b8] overflow-hidden`}>
          <div className="col-span-1"></div>
          <div className="col-span-3 font-mono font-bold">CASH</div>
          <div className="col-span-1 text-right tabular-nums">{(portfolio.cashWeight * 100).toFixed(1)}%</div>
          <div className={`${showLots ? 'col-span-18' : 'col-span-16'} text-right text-[9px] text-[#64748b] self-center pr-2`}>
            uninvested · earns 0
          </div>
        </div>
      )}

      {/* Weighted-average portfolio row — Steve directive 2026-05-15:
          "ive now asked 10x to include the portfolio average". Row was
          rendering but bg-[#0a0e14] matched the page background, making it
          near-invisible. Bumped contrast (amber-tinted bg + thicker amber
          top border + taller h-[32px]) so it can't blend in again. */}
      <div className={`grid ${gridColsCls} gap-1 px-2 h-[32px] text-[9px] font-bold leading-tight items-center border-t-2 border-[#c9a84c]/60 bg-[#1a1410] overflow-hidden`}>
        <div className="col-span-1"></div>
        <div className="col-span-3 font-mono text-amber-400">PORTFOLIO</div>
        {(() => {
          // Avg market cap of holdings — simple mean across positions
          // with a non-null mktCap. Index ETFs excluded.
          const caps = holdings
            .filter((h) => !INDEX_ETFS.has(h.symbol))
            .map((h) => scores?.get(h.symbol)?.marketCap ?? null)
            .filter((mc): mc is number => mc != null && mc > 0);
          const avgMc = caps.length > 0 ? caps.reduce((a, b) => a + b, 0) / caps.length : null;
          return (
            <div className="col-span-1 text-right tabular-nums text-[#94a3b8]" title="Simple average market cap across holdings">
              {avgMc != null ? `$${Math.round(avgMc / 1e9)}B` : '—'}
            </div>
          );
        })()}
        <div className="col-span-1 text-right tabular-nums text-white">{(totalWeight * 100).toFixed(1)}%</div>
        {showLots && (
          <div className="col-span-2 text-[8px] text-[#64748b] self-center font-normal text-right pr-1">weighted avg</div>
        )}
        {/* Px — placeholder */}
        <div className="col-span-1 text-right text-[#475569]">—</div>
        {/* %1D */}
        <PnlPortfolioCell wa={totals.day} />
        {/* MTD / YTD / 52wH — weighted-avg portfolio cells */}
        <PnlPortfolioCell wa={totals.mtd} />
        <PnlPortfolioCell wa={totals.ytd} />
        <PnlPortfolioCell wa={totals.off52} />
        {/* FV$, 1Y%, 3Y$, 3Y%, Asym — placeholders */}
        <div className="col-span-1 text-right text-[#475569]">—</div>
        <div className="col-span-1 text-right text-[#475569]">—</div>
        <div className="col-span-1 text-right text-[#475569]">—</div>
        <div className="col-span-1 text-right text-[#475569]">—</div>
        <div className="col-span-1 text-right text-[#475569]">—</div>
        {/* C now */}
        <div className={`col-span-1 text-right tabular-nums ${totals.composite.value != null ? compTier(totals.composite.value).color : 'text-[#94a3b8]'}`}>
          {totals.composite.value == null ? '—' : totals.composite.value.toFixed(2)}
        </div>
        {/* nextQC — portfolio-level placeholder */}
        <div className="col-span-1 text-right text-[#475569]">—</div>
        {/* Cong / Insid / Call / Earn — placeholders */}
        <div className="col-span-1 text-right text-[#475569]">—</div>
        <div className="col-span-1 text-right text-[#475569]">—</div>
        <div className="col-span-1 text-right text-[#475569]">—</div>
        <div className="col-span-1 text-right text-[#475569]">—</div>
        {/* Hedge — placeholder */}
        <div className="col-span-1 text-center text-[#64748b] font-normal">—</div>
        {/* Tier label */}
        <div className="col-span-1 text-right text-[7px] uppercase tracking-wide self-center text-[#64748b] font-normal">
          {totals.composite.value != null ? compTier(totals.composite.value).label : '—'}
        </div>
        {/* αDays — placeholder */}
        <div className="col-span-1 text-right text-[#475569]">—</div>
      </div>
    </>
  );
}
